import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Collapse,
  FormControl,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import NotificationsNoneIcon from '@mui/icons-material/NotificationsNone';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PhoneIphoneIcon from '@mui/icons-material/PhoneIphone';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import axios from 'axios';
import { useTranslation } from 'react-i18next';

import SmsSegmentCounter from './SmsSegmentCounter';
import { calculateSmsSegments } from '../../utils/smsSegments';
import { formatCost } from '../service_reminders/reminderUtils';

// Customer reminder / notification section shared by every schedule dialog
// (calendar desktop, calendar mobile, work-order quick schedule — and any
// future unified work-order-creation dialog). Deliberately self-contained so a
// host dialog needs exactly three lines and zero knowledge of the internals:
//
//   const reminders = useScheduleReminders({
//     open,                       // dialog open state (resets + refetches on open)
//     maintenanceId,              // target work order, or null when it doesn't exist yet
//     scheduleId,                 // the schedule being EDITED, or null when creating
//   });
//   <ScheduleReminderSection {...reminders} disabled={saving} contactOverride={...} />
//   await reminders.save(savedScheduleId);   // AFTER the schedule POST/PATCH succeeds
//
// contactOverride ({phone, email}) is only for flows where the maintenance does
// not exist yet (contact comes from the dialog's own customer inputs); with a
// maintenanceId the hook fetches contact info + the booking guard itself.
// save() never throws — it returns [{kind, code}] failures. The section renders
// nothing when the org has the feature off, and renders the booking-guard info
// row instead of controls for booking-linked work orders.
//
// The two message kinds (pre-appointment reminder, immediate confirmation) are
// deliberately symmetric: both expand to the same channel select, SMS cost line
// and message-override fields, differing only in the reminder's timing select.

export const OFFSET_OPTIONS = [1, 2, 4, 24, 48, 72];

const EMPTY_FORM = {
  reminderEnabled: false,
  offsetHours: 24,
  channel: 'both',
  smsBody: '',
  emailSubject: '',
  emailBody: '',
  notificationEnabled: false,
  notificationChannel: 'both',
  notificationSmsBody: '',
  notificationEmailSubject: '',
  notificationEmailBody: '',
};

export function useScheduleReminders({ open, maintenanceId, scheduleId }) {
  const [config, setConfig] = useState(null);
  const [context, setContext] = useState(null);
  const [existingReminders, setExistingReminders] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [prefilled, setPrefilled] = useState(false);

  // Config once per dialog open (org defaults, texts, SMS price/trial state)
  useEffect(() => {
    if (!open) {
      setConfig(null);
      setForm(EMPTY_FORM);
      setPrefilled(false);
      return;
    }
    let cancelled = false;
    axios
      .get('/api/scheduling/reminder-config/')
      .then((res) => {
        if (!cancelled) setConfig(res.data);
      })
      .catch(() => {
        if (!cancelled) setConfig(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open]);

  // Contact info + booking guard for the target maintenance
  useEffect(() => {
    if (!open || !maintenanceId) {
      setContext(null);
      return;
    }
    let cancelled = false;
    axios
      .get('/api/scheduling/schedules/reminder-context/', { params: { maintenance: maintenanceId } })
      .then((res) => {
        if (!cancelled) setContext(res.data);
      })
      .catch(() => {
        if (!cancelled) setContext(null);
      });
    return () => {
      cancelled = true;
    };
  }, [open, maintenanceId]);

  // Existing reminder rows when editing a schedule
  useEffect(() => {
    if (!open || !scheduleId) {
      setExistingReminders(scheduleId ? null : []);
      return;
    }
    let cancelled = false;
    axios
      .get('/api/scheduling/reminders/', { params: { schedule: scheduleId } })
      .then((res) => {
        if (!cancelled) setExistingReminders(Array.isArray(res.data) ? res.data : []);
      })
      .catch(() => {
        if (!cancelled) setExistingReminders([]);
      });
    return () => {
      cancelled = true;
    };
  }, [open, scheduleId]);

  // Prefill once per open: from the pending rows (edit) or org defaults (create)
  useEffect(() => {
    if (!open || prefilled || !config) return;
    if (scheduleId && existingReminders === null) return; // wait for rows
    if (scheduleId) {
      const rows = existingReminders || [];
      const pendingReminder = rows.find((r) => r.kind === 'reminder' && r.status === 'scheduled');
      const pendingNotification = rows.find((r) => r.kind === 'notification' && r.status === 'scheduled');
      setForm({
        ...EMPTY_FORM,
        reminderEnabled: !!pendingReminder,
        offsetHours: pendingReminder?.offset_hours ?? config.default_offset_hours ?? 24,
        channel: pendingReminder?.channel || config.default_channel || 'both',
        smsBody: pendingReminder?.sms_body || '',
        emailSubject: pendingReminder?.email_subject || '',
        emailBody: pendingReminder?.email_body || '',
        notificationEnabled: !!pendingNotification,
        notificationChannel: pendingNotification?.channel || config.default_channel || 'both',
        notificationSmsBody: pendingNotification?.sms_body || '',
        notificationEmailSubject: pendingNotification?.email_subject || '',
        notificationEmailBody: pendingNotification?.email_body || '',
      });
    } else {
      setForm({
        ...EMPTY_FORM,
        reminderEnabled: !!config.default_reminder_enabled,
        offsetHours: config.default_offset_hours ?? 24,
        channel: config.default_channel || 'both',
        notificationEnabled: !!config.default_notification_enabled,
        notificationChannel: config.default_channel || 'both',
      });
    }
    setPrefilled(true);
  }, [open, prefilled, config, scheduleId, existingReminders]);

  const visible = !!config && config.is_enabled !== false;
  const blockedByBooking = !!context?.has_active_booking;

  // Persist the reminder state against a saved schedule. Never throws — returns
  // an array of {kind, code} failures the caller may surface (empty = all good).
  const save = async (savedScheduleId) => {
    if (!visible || blockedByBooking || !savedScheduleId) return [];
    const failures = [];
    const rows = existingReminders || [];

    // Symmetric per-kind logic: enabled → PATCH the pending row or POST a new
    // one; disabled → cancel the pending row. Sent rows are audit and stay put.
    const saveKind = async (kind, enabled, payload) => {
      const pending = rows.find((r) => r.kind === kind && r.status === 'scheduled');
      try {
        if (enabled) {
          if (pending) {
            await axios.patch(`/api/scheduling/reminders/${pending.id}/`, payload);
          } else {
            await axios.post('/api/scheduling/reminders/', { schedule: savedScheduleId, kind, ...payload });
          }
        } else if (pending) {
          await axios.delete(`/api/scheduling/reminders/${pending.id}/`);
        }
      } catch (err) {
        failures.push({ kind, code: err.response?.data?.error_code || 'unknown' });
      }
    };

    await saveKind('reminder', form.reminderEnabled, {
      offset_hours: form.offsetHours,
      channel: form.channel,
      sms_body: form.smsBody,
      email_subject: form.emailSubject,
      email_body: form.emailBody,
    });
    await saveKind('notification', form.notificationEnabled, {
      channel: form.notificationChannel,
      sms_body: form.notificationSmsBody,
      email_subject: form.notificationEmailSubject,
      email_body: form.notificationEmailBody,
    });

    if (failures.length) {
      console.warn('Schedule reminder save failures:', failures);
    }
    return failures;
  };

  return { config, context, existingReminders, form, setForm, visible, blockedByBooking, save };
}

const channelIncludesSms = (channel) => channel === 'sms' || channel === 'both';
const channelIncludesEmail = (channel) => channel === 'email' || channel === 'both';

function RecipientLine({ t, contact, needsSms, needsEmail }) {
  if (!contact) return null;
  const missingSms = needsSms && !contact.phone;
  const missingEmail = needsEmail && !contact.email;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
      {contact.phone && (
        <Chip icon={<PhoneIphoneIcon />} label={contact.phone} size="small" variant="outlined" />
      )}
      {contact.email && (
        <Chip icon={<MailOutlineIcon />} label={contact.email} size="small" variant="outlined" />
      )}
      {(missingSms || missingEmail) && (
        <Typography variant="caption" sx={{ color: 'warning.main' }}>
          {missingSms && missingEmail
            ? t('schedule_reminders.missing_contact_both')
            : missingSms
              ? t('schedule_reminders.missing_contact_phone')
              : t('schedule_reminders.missing_contact_email')}
        </Typography>
      )}
    </Box>
  );
}

// Channel select + SMS segment/cost line + collapsible message override — the
// identical inner controls of both kinds. `fields` maps to the form keys and
// `defaults` to the org/built-in texts of that kind.
function ChannelAndTexts({ t, kind, form, update, fields, config, defaults, disabled }) {
  const [customizeOpen, setCustomizeOpen] = useState(false);

  const smsAllowed = config?.sms_allowed !== false;
  const smsPrice = config?.sms_price_per_segment ? Number(config.sms_price_per_segment) : null;
  const smsPrefix = config?.sms_prefix || '';
  const channel = form[fields.channel];

  const effectiveSmsBody = form[fields.smsBody] || defaults.orgSms || defaults.builtinSms || '';
  const assembledSms = effectiveSmsBody ? `${smsPrefix}${effectiveSmsBody}` : '';
  const smsSegments = assembledSms ? calculateSmsSegments(assembledSms).segmentCount : 0;

  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, mt: 1.5, ml: 3.5, flexWrap: 'wrap', alignItems: 'center' }}>
        {fields.offset && (
          <>
            <FormControl size="small" sx={{ minWidth: 130 }}>
              <Select
                value={OFFSET_OPTIONS.includes(form[fields.offset]) ? form[fields.offset] : 'custom'}
                onChange={(e) => {
                  if (e.target.value !== 'custom') update({ [fields.offset]: e.target.value });
                  else update({ [fields.offset]: 12 });
                }}
                disabled={disabled}
                inputProps={{ 'data-testid': `${kind}-offset-select` }}
              >
                {OFFSET_OPTIONS.map((h) => (
                  <MenuItem key={h} value={h}>
                    {t('schedule_reminders.offset_option', { hours: h })}
                  </MenuItem>
                ))}
                <MenuItem value="custom">{t('schedule_reminders.offset_custom')}</MenuItem>
              </Select>
            </FormControl>
            {!OFFSET_OPTIONS.includes(form[fields.offset]) && (
              <TextField
                size="small"
                type="number"
                value={form[fields.offset]}
                onChange={(e) => {
                  const val = parseInt(e.target.value, 10);
                  update({ [fields.offset]: Number.isNaN(val) ? '' : Math.max(1, Math.min(336, val)) });
                }}
                label={t('schedule_reminders.offset_hours_label')}
                sx={{ width: 120 }}
                inputProps={{ min: 1, max: 336 }}
              />
            )}
          </>
        )}
        <FormControl size="small" sx={{ minWidth: 170 }}>
          <Select
            value={channel}
            onChange={(e) => update({ [fields.channel]: e.target.value })}
            disabled={disabled}
            inputProps={{ 'data-testid': `${kind}-channel-select` }}
          >
            <MenuItem value="both" disabled={!smsAllowed}>
              {t('schedule_reminders.channel_both')}
            </MenuItem>
            <MenuItem value="sms" disabled={!smsAllowed}>
              {t('schedule_reminders.channel_sms')}
            </MenuItem>
            <MenuItem value="email">{t('schedule_reminders.channel_email')}</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {channelIncludesSms(channel) && smsAllowed && (
        <Box sx={{ ml: 3.5, mt: 0.5 }}>
          <SmsSegmentCounter prefix={smsPrefix} text={effectiveSmsBody} defaultMessage="" />
          {smsPrice !== null && smsSegments > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
              {t('schedule_reminders.sms_cost_estimate', { cost: formatCost(smsPrice * smsSegments) })}
            </Typography>
          )}
        </Box>
      )}
      {!smsAllowed && (
        <Alert severity="info" sx={{ ml: 3.5, mt: 1, '& .MuiAlert-message': { fontSize: '12px' } }}>
          {t('schedule_reminders.sms_trial_blocked')}
        </Alert>
      )}

      <Box sx={{ ml: 3.5, mt: 1 }}>
        <Typography
          variant="caption"
          onClick={() => setCustomizeOpen((prev) => !prev)}
          sx={{
            color: 'secondary.main',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25,
            userSelect: 'none',
          }}
          data-testid={`${kind}-customize-toggle`}
        >
          {customizeOpen ? <ExpandLessIcon sx={{ fontSize: 16 }} /> : <ExpandMoreIcon sx={{ fontSize: 16 }} />}
          {t('schedule_reminders.customize_message')}
        </Typography>
        <Collapse in={customizeOpen}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {t('schedule_reminders.placeholders_help')}{' '}
              <code style={{ fontSize: '11px' }}>{(config?.placeholders || []).join(' ')}</code>
            </Typography>
            {channelIncludesSms(channel) && (
              <TextField
                label={t('schedule_reminders.sms_body')}
                multiline
                rows={2}
                size="small"
                fullWidth
                value={form[fields.smsBody]}
                onChange={(e) => update({ [fields.smsBody]: e.target.value })}
                placeholder={defaults.orgSms || defaults.builtinSms || ''}
                helperText={t('schedule_reminders.override_help')}
              />
            )}
            {channelIncludesEmail(channel) && (
              <>
                <TextField
                  label={t('schedule_reminders.email_subject')}
                  size="small"
                  fullWidth
                  value={form[fields.emailSubject]}
                  onChange={(e) => update({ [fields.emailSubject]: e.target.value })}
                  placeholder={defaults.orgEmailSubject || defaults.builtinEmailSubject || ''}
                  inputProps={{ maxLength: 255 }}
                />
                <TextField
                  label={t('schedule_reminders.email_body')}
                  multiline
                  rows={4}
                  size="small"
                  fullWidth
                  value={form[fields.emailBody]}
                  onChange={(e) => update({ [fields.emailBody]: e.target.value })}
                  placeholder={defaults.orgEmailBody || defaults.builtinEmailBody || ''}
                />
              </>
            )}
          </Box>
        </Collapse>
      </Box>
    </>
  );
}

export default function ScheduleReminderSection({
  config,
  context,
  existingReminders,
  form,
  setForm,
  visible,
  blockedByBooking,
  contactOverride = null,
  disabled = false,
}) {
  const { t } = useTranslation();

  if (!visible) return null;

  if (blockedByBooking) {
    return (
      <Alert
        severity="info"
        icon={<EventAvailableIcon fontSize="inherit" />}
        sx={{ mt: 2, borderRadius: '10px', '& .MuiAlert-message': { fontSize: '13px' } }}
        data-testid="reminder-booking-guard"
      >
        {t('schedule_reminders.handled_by_booking')}
      </Alert>
    );
  }

  const contact = contactOverride || context?.customer || null;
  const rows = existingReminders || [];
  const sentReminder = rows.find((r) => r.kind === 'reminder' && r.status === 'sent');
  const sentNotification = rows.find((r) => r.kind === 'notification' && r.status === 'sent');

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const formatSentAt = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString('fi-FI', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const sentLine = (labelKey, sentRow) => (
    <Typography
      variant="caption"
      sx={{ color: 'success.main', display: 'flex', alignItems: 'center', gap: 0.5, ml: 3.5 }}
    >
      <CheckCircleIcon sx={{ fontSize: 14 }} />
      {t(labelKey, { time: formatSentAt(sentRow.sent_at) })}
    </Typography>
  );

  const needsSms =
    (form.reminderEnabled && channelIncludesSms(form.channel)) ||
    (form.notificationEnabled && channelIncludesSms(form.notificationChannel));
  const needsEmail =
    (form.reminderEnabled && channelIncludesEmail(form.channel)) ||
    (form.notificationEnabled && channelIncludesEmail(form.notificationChannel));

  return (
    <Box
      sx={{ mt: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: '10px' }}
      data-testid="schedule-reminder-section"
    >
      {/* Pre-appointment reminder */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
        <NotificationsNoneIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 500, flex: 1, minWidth: 140 }}>
          {t('schedule_reminders.remind_customer')}
        </Typography>
        <Switch
          checked={form.reminderEnabled}
          onChange={(e) => update({ reminderEnabled: e.target.checked })}
          disabled={disabled}
          size="small"
          inputProps={{ 'data-testid': 'reminder-toggle' }}
        />
      </Box>
      {sentReminder && sentLine('schedule_reminders.reminder_sent_at', sentReminder)}
      <Collapse in={form.reminderEnabled}>
        <ChannelAndTexts
          t={t}
          kind="reminder"
          form={form}
          update={update}
          config={config}
          disabled={disabled}
          fields={{
            offset: 'offsetHours',
            channel: 'channel',
            smsBody: 'smsBody',
            emailSubject: 'emailSubject',
            emailBody: 'emailBody',
          }}
          defaults={{
            orgSms: config?.reminder_sms_text,
            builtinSms: config?.default_texts?.reminder?.sms,
            orgEmailSubject: config?.reminder_email_subject,
            builtinEmailSubject: config?.default_texts?.reminder?.email_subject,
            orgEmailBody: config?.reminder_email_text,
            builtinEmailBody: config?.default_texts?.reminder?.email_body,
          }}
        />
      </Collapse>

      {/* Immediate confirmation */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', mt: 1 }}>
        <EventAvailableIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
        <Typography sx={{ fontSize: '14px', fontWeight: 500, flex: 1, minWidth: 140 }}>
          {t('schedule_reminders.notify_now')}
        </Typography>
        <Switch
          checked={form.notificationEnabled}
          onChange={(e) => update({ notificationEnabled: e.target.checked })}
          disabled={disabled}
          size="small"
          inputProps={{ 'data-testid': 'notification-toggle' }}
        />
      </Box>
      {sentNotification && sentLine('schedule_reminders.notification_sent_at', sentNotification)}
      <Collapse in={form.notificationEnabled}>
        <ChannelAndTexts
          t={t}
          kind="notification"
          form={form}
          update={update}
          config={config}
          disabled={disabled}
          fields={{
            channel: 'notificationChannel',
            smsBody: 'notificationSmsBody',
            emailSubject: 'notificationEmailSubject',
            emailBody: 'notificationEmailBody',
          }}
          defaults={{
            orgSms: config?.notification_sms_text,
            builtinSms: config?.default_texts?.notification?.sms,
            orgEmailSubject: config?.notification_email_subject,
            builtinEmailSubject: config?.default_texts?.notification?.email_subject,
            orgEmailBody: config?.notification_email_text,
            builtinEmailBody: config?.default_texts?.notification?.email_body,
          }}
        />
      </Collapse>

      {(form.reminderEnabled || form.notificationEnabled) && (
        <RecipientLine t={t} contact={contact} needsSms={needsSms} needsEmail={needsEmail} />
      )}
    </Box>
  );
}
